import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useResearchOptions, useSearchCities } from '../../hooks/useLeadsVoQueries';
import { hasPuretechExcluded, togglePuretechExclusion } from '../../utils/leadsVoMotorisations';

const chipSelected = { backgroundColor: '#262626', color: 'white', fontWeight: 600 };
const chipUnselected = { backgroundColor: 'white', color: '#262626', border: '1px solid #E0E0E0', '&:hover': { borderColor: '#FDBB5F' } };

const RADIUS_OPTIONS = [1, 3, 5, 10, 20, 50];
const WEEKDAYS = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

type Slot = { start: string; end: string };
export type BusinessHours = Record<string, Slot[]>;

export interface ZoneValue {
  mode: 'cities' | 'radius';
  cities?: string[];
  center?: string;
  radius_km?: number | null;
}

interface AgentCriteriaEditorProps {
  initialZone: ZoneValue | null;
  initialResearch: Record<string, any> | null;
  initialHours: BusinessHours | null;
  onSaveZone: (payload: Record<string, any>) => Promise<unknown>;
  onSaveResearch: (payload: Record<string, any>) => Promise<unknown>;
  onSaveHours: (hours: BusinessHours) => Promise<unknown>;
}

// Éditeur des paramètres d'un agent (zone, critères véhicules, horaires).
// Utilisé par la page Réglages agence et par la fiche agent admin — les
// callbacks de sauvegarde portent la différence (endpoints agence vs admin).
const AgentCriteriaEditor = ({
  initialZone,
  initialResearch,
  initialHours,
  onSaveZone,
  onSaveResearch,
  onSaveHours,
}: AgentCriteriaEditorProps) => {
  const { data: options } = useResearchOptions();

  const [zone, setZone] = useState<any>({ mode: 'cities', cities: [], center: '', radius_km: null });
  const [research, setResearch] = useState<any>({
    marques_voulues: [], carburants: [], boites: [],
    types_carrosserie: [], prix_min_eur: null, prix_max_eur: null,
    annee_min: null, annee_max: null, km_min: null, km_max: null,
    exclure_accidentes: false, exclure_import: false, motorisations_exclues: [],
  });
  const [hours, setHours] = useState<BusinessHours>({});
  const [cityQuery, setCityQuery] = useState('');
  const { data: citiesData } = useSearchCities(cityQuery);

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (loaded || !options) return;

    // Normalise : la zone peut venir du partenaire ou d'une config locale partielle
    // ({mode, cities} ou {mode, center, radius_km}) — tous les champs doivent exister.
    if (initialZone) {
      setZone({
        mode: initialZone.mode ?? 'cities',
        cities: initialZone.cities ?? [],
        center: initialZone.center ?? '',
        radius_km: initialZone.radius_km ?? null,
      });
    }
    if (initialHours && Object.keys(initialHours).length > 0) setHours(initialHours);
    if (initialResearch) {
      const cv = initialResearch;
      setResearch({
        marques_voulues: cv.marques_voulues?.length > 0 ? cv.marques_voulues : [...options.brands],
        carburants: cv.carburants?.length > 0 ? cv.carburants : [...options.carburants],
        boites: cv.boites?.length > 0 ? cv.boites : [...options.boites],
        types_carrosserie: cv.types_carrosserie?.length > 0 ? cv.types_carrosserie : [...options.types_carrosserie],
        prix_min_eur: cv.prix_min_eur ?? null,
        prix_max_eur: cv.prix_max_eur ?? null,
        annee_min: cv.annee_min ?? null,
        annee_max: cv.annee_max ?? null,
        km_min: cv.km_min ?? null,
        km_max: cv.km_max ?? null,
        exclure_accidentes: cv.exclure_accidentes ?? false,
        exclure_import: cv.exclure_import ?? false,
        motorisations_exclues: cv.motorisations_exclues ?? [],
      });
    }
    setLoaded(true);
  }, [initialZone, initialResearch, initialHours, options, loaded]);

  const toggleArray = (field: string, value: string) => {
    const cur = research[field] || [];
    setResearch({ ...research, [field]: cur.includes(value) ? cur.filter((x: string) => x !== value) : [...cur, value] });
  };

  const saveZone = async () => {
    try {
      setSaving('zone'); setError('');
      if (zone.mode === 'cities') {
        await onSaveZone({ mode: 'cities', cities: zone.cities });
      } else {
        await onSaveZone({ mode: 'radius', center: zone.center, radius_km: zone.radius_km });
      }
      setSuccess('Zone enregistrée');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur');
    } finally { setSaving(''); }
  };

  const saveResearch = async () => {
    try {
      setSaving('research'); setError('');
      await onSaveResearch({ criteres_vehicule: research });
      setSuccess('Critères enregistrés');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur');
    } finally { setSaving(''); }
  };

  const saveHours = async () => {
    try {
      setSaving('hours'); setError('');
      await onSaveHours(hours);
      setSuccess('Horaires enregistrés');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur');
    } finally { setSaving(''); }
  };

  const toggleOpen = (day: string) => {
    if ((hours[day] || []).length === 0) {
      setHours({ ...hours, [day]: [{ start: '09:00', end: '18:00' }] });
    } else {
      setHours({ ...hours, [day]: [] });
    }
  };

  const addSlot = (day: string) => {
    const cur = hours[day] || [];
    if (cur.length >= 2) return;
    setHours({ ...hours, [day]: [...cur, { start: '14:00', end: '18:00' }] });
  };

  const removeSlot = (day: string, idx: number) => {
    setHours({ ...hours, [day]: (hours[day] || []).filter((_, i) => i !== idx) });
  };

  const updateSlot = (day: string, idx: number, field: 'start' | 'end', value: string) => {
    const cur = [...(hours[day] || [])];
    cur[idx] = { ...cur[idx], [field]: value };
    setHours({ ...hours, [day]: cur });
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Zone */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Zone géographique</Typography>
          <Button onClick={saveZone} disabled={saving === 'zone'} sx={{ backgroundColor: '#FDBB5F', color: '#262626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fcae4d' } }}>
            {saving === 'zone' ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Box>
        <RadioGroup value={zone.mode} onChange={(e) => setZone({ ...zone, mode: e.target.value })} sx={{ mb: 2 }}>
          <FormControlLabel value="cities" control={<Radio sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />} label="Par villes / codes postaux" />
          <FormControlLabel value="radius" control={<Radio sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />} label="Par rayon autour d'une ville" />
        </RadioGroup>
        {zone.mode === 'cities' && (
          <Autocomplete
            multiple freeSolo options={citiesData?.cities ?? []} value={zone.cities}
            onChange={(_, val) => setZone({ ...zone, cities: val })}
            onInputChange={(_, val) => setCityQuery(val)}
            renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} sx={{ backgroundColor: '#262626', color: 'white' }} />)}
            renderInput={(params) => <TextField {...params} placeholder="Rechercher une ville ou un code postal" />}
          />
        )}
        {zone.mode === 'radius' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              freeSolo options={citiesData?.cities ?? []} value={zone.center}
              onChange={(_, val) => setZone({ ...zone, center: val })}
              onInputChange={(_, val) => setCityQuery(val)}
              renderInput={(params) => <TextField {...params} label="Ville" placeholder="Ex: Bordeaux (33000)" />}
            />
            <Box>
              <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#464646', mb: 1 }}>Rayon</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {RADIUS_OPTIONS.map((km) => (
                  <Chip key={km} label={`${km} km`} onClick={() => setZone({ ...zone, radius_km: km })}
                    sx={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, ...(zone.radius_km === km ? chipSelected : chipUnselected) }} />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Critères véhicules */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Critères véhicules</Typography>
          <Button onClick={saveResearch} disabled={saving === 'research'} sx={{ backgroundColor: '#FDBB5F', color: '#262626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fcae4d' } }}>
            {saving === 'research' ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Box>

        {options && (
          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography sx={{ fontSize: 13, color: '#464646', fontWeight: 600 }}>Marques ({research.marques_voulues.length}/{options.brands.length})</Typography>
                <Box display="flex" gap={2}>
                  <Button size="small" onClick={() => setResearch({ ...research, marques_voulues: [...options.brands] })} sx={{ textTransform: 'none', color: '#FDBB5F', fontSize: 12, minWidth: 'auto' }}>Tout sélectionner</Button>
                  <Button size="small" onClick={() => setResearch({ ...research, marques_voulues: [] })} sx={{ textTransform: 'none', color: '#9E9E9E', fontSize: 12, minWidth: 'auto' }}>Tout désélectionner</Button>
                </Box>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1.5, borderRadius: 1 }}>
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {options.brands.map((b: string) => {
                    const on = research.marques_voulues.includes(b);
                    return <Chip key={b} label={on ? `✓ ${b}` : b} onClick={() => toggleArray('marques_voulues', b)} size="small" sx={{ cursor: 'pointer', fontSize: 11, ...(on ? chipSelected : chipUnselected) }} />;
                  })}
                </Box>
              </Paper>
            </Box>

            <Autocomplete multiple options={options.carburants} value={research.carburants} onChange={(_, val) => setResearch({ ...research, carburants: val })} renderInput={(params) => <TextField {...params} label="Carburants" />} />
            <Autocomplete multiple options={options.boites} value={research.boites} onChange={(_, val) => setResearch({ ...research, boites: val })} renderInput={(params) => <TextField {...params} label="Boîte de vitesses" />} />
            <Autocomplete multiple options={options.types_carrosserie} value={research.types_carrosserie} onChange={(_, val) => setResearch({ ...research, types_carrosserie: val })} renderInput={(params) => <TextField {...params} label="Types de carrosserie" />} />

            <Grid container spacing={2}>
              <Grid item xs={6}><TextField label="Année min (optionnel)" type="number" value={research.annee_min ?? ''} onChange={(e) => setResearch({ ...research, annee_min: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField label="Année max (optionnel)" type="number" value={research.annee_max ?? ''} onChange={(e) => setResearch({ ...research, annee_max: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField label="Kilométrage min (optionnel)" type="number" value={research.km_min ?? ''} onChange={(e) => setResearch({ ...research, km_min: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField label="Kilométrage max (optionnel)" type="number" value={research.km_max ?? ''} onChange={(e) => setResearch({ ...research, km_max: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField label="Prix min (€, optionnel)" type="number" value={research.prix_min_eur ?? ''} onChange={(e) => setResearch({ ...research, prix_min_eur: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField label="Prix max (€, optionnel)" type="number" value={research.prix_max_eur ?? ''} onChange={(e) => setResearch({ ...research, prix_max_eur: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" /></Grid>
            </Grid>

            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les véhicules accidentés</Typography>
                <Switch checked={research.exclure_accidentes} onChange={(e) => setResearch({ ...research, exclure_accidentes: e.target.checked })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les véhicules d'import</Typography>
                <Switch checked={research.exclure_import} onChange={(e) => setResearch({ ...research, exclure_import: e.target.checked })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les moteurs PureTech</Typography>
                <Switch checked={hasPuretechExcluded(research.motorisations_exclues)} onChange={(e) => setResearch({ ...research, motorisations_exclues: togglePuretechExclusion(research.motorisations_exclues, e.target.checked) })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Horaires */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Horaires d'ouverture</Typography>
          <Button onClick={saveHours} disabled={saving === 'hours'} sx={{ backgroundColor: '#FDBB5F', color: '#262626', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fcae4d' } }}>
            {saving === 'hours' ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Button size="small" onClick={() => { const m = hours.lundi || []; setHours({ ...hours, mardi: [...m], mercredi: [...m], jeudi: [...m], vendredi: [...m] }); }}
            sx={{ textTransform: 'none', color: '#FDBB5F', fontWeight: 600, fontSize: 12 }}>↻ Copier lundi sur mar-ven</Button>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
          {WEEKDAYS.map((d) => {
            const slots = hours[d.key] || [];
            const isOpen = slots.length > 0;
            return (
              <Box key={d.key} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: '1px solid #F0F0F0' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#262626', minWidth: 80 }}>{d.label}</Typography>
                <FormControlLabel
                  control={<Checkbox checked={isOpen} onChange={() => toggleOpen(d.key)} size="small" sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />}
                  label={<Typography sx={{ fontSize: 12, color: '#464646' }}>{isOpen ? 'Ouvert' : 'Fermé'}</Typography>}
                  sx={{ minWidth: 90 }}
                />
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1}>
                  {isOpen && slots.map((s, idx) => (
                    <Box key={idx} display="flex" alignItems="center" gap={0.5}>
                      <TextField type="time" value={s.start} onChange={(e) => updateSlot(d.key, idx, 'start', e.target.value)} size="small" sx={{ width: 110 }} inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }} />
                      <Typography sx={{ color: '#464646', fontSize: 12 }}>–</Typography>
                      <TextField type="time" value={s.end} onChange={(e) => updateSlot(d.key, idx, 'end', e.target.value)} size="small" sx={{ width: 110 }} inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }} />
                      {slots.length > 1 && <Button size="small" onClick={() => removeSlot(d.key, idx)} sx={{ color: '#C62828', minWidth: 'auto', fontSize: 12, p: 0 }}>×</Button>}
                    </Box>
                  ))}
                  {isOpen && slots.length < 2 && (
                    <Button size="small" onClick={() => addSlot(d.key)} sx={{ textTransform: 'none', color: '#FDBB5F', fontWeight: 600, fontSize: 12 }}>+ plage</Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Paper>

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} message={success} />
    </>
  );
};

export default AgentCriteriaEditor;
